import { API } from '../config';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { NotificationBell } from '../components/NotificationBell';
import { PanicButton } from '../components/PanicButton';
import { ChatView } from '../components/ChatView';
import { TelegramUserChats } from '../components/TelegramUserChats';
import { WhatsAppChats } from '../components/WhatsAppChats';
import { WhatsAppWebView } from '../components/WhatsAppWebView';
import { SearchOverlay } from '../components/SearchOverlay';
import { AppLockScreen } from '../components/AppLockScreen';
import { SettingsDialog } from '../components/SettingsDialog';
import {
  MessageCircle, Search, LogOut, User, Users, Wifi, WifiOff, Hash,
  Pin, Star, MoreVertical, Settings, Shield, FileText, Phone, Zap
} from 'lucide-react';
import { toast } from 'sonner';


export default function ChatApp() {
  const { user, token, logout, getAuthHeaders } = useAuth();
  const { isConnected, connectionState, lastMessage } = useWebSocket(token);
  const navigate = useNavigate();

  const [platform, setPlatform] = useState('telegram');
  const [chatMode, setChatModeRaw] = useState(() => {
    const saved = localStorage.getItem('chatMode');
    return (saved === 'bot') ? 'accounts' : (saved || 'accounts');
  });
  const setChatMode = (m) => { setChatModeRaw(m); localStorage.setItem('chatMode', m); };
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [appLocked, setAppLocked] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  // Multi-account state
  const [tgAccounts, setTgAccounts] = useState([]);
  const [selectedTgAccount, setSelectedTgAccount] = useState('all');
  const [tgUnreadCounts, setTgUnreadCounts] = useState({}); // session_id -> count // 'all' or session_id

  useEffect(() => {
    if (user?.app_lock_enabled) {
      const lastUnlock = sessionStorage.getItem('app_unlocked_at');
      if (!lastUnlock) setAppLocked(true);
      else if ((Date.now() - parseInt(lastUnlock)) / 1000 > (user.app_lock_timeout_seconds || 300)) setAppLocked(true);
    }
  }, [user]);

  useEffect(() => {
    if (!user?.app_lock_enabled) return;
    const timeout = (user.app_lock_timeout_seconds || 300) * 1000;
    const interval = setInterval(() => { if (Date.now() - lastActivity > timeout) setAppLocked(true); }, 10000);
    return () => clearInterval(interval);
  }, [user, lastActivity]);

  useEffect(() => {
    const handler = () => setLastActivity(Date.now());
    window.addEventListener('mousemove', handler);
    window.addEventListener('keydown', handler);
    window.addEventListener('click', handler);
    return () => { window.removeEventListener('mousemove', handler); window.removeEventListener('keydown', handler); window.removeEventListener('click', handler); };
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/conversations?platform=${platform}`, { headers: getAuthHeaders() });
      setConversations(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Oturum sona erdi veya hesap kilitlendi');
        navigate('/');
      }
    }
  }, [platform, getAuthHeaders, navigate]);

  useEffect(() => { if (!appLocked) fetchConversations(); }, [platform, appLocked, fetchConversations]);

  // Fetch Telegram accounts + unread counts when in user mode
  useEffect(() => {
    if (chatMode === 'user') {
      axios.get(`${API}/telegram-user/accounts`, { headers: getAuthHeaders() })
        .then(res => setTgAccounts((res.data.accounts || []).filter(a => a.status === 'active')))
        .catch(() => {});
      // Fetch unread counts
      axios.get(`${API}/telegram-user/unread-counts`, { headers: getAuthHeaders() })
        .then(res => {
          const counts = {};
          (res.data.accounts || []).forEach(a => { counts[a.session_id] = a.unread_count; });
          setTgUnreadCounts(counts);
        })
        .catch(() => {});
    }
  }, [chatMode, getAuthHeaders]);

  // Poll unread counts every 30s in user mode
  useEffect(() => {
    if (chatMode !== 'user') return;
    const t = setInterval(() => {
      axios.get(`${API}/telegram-user/unread-counts`, { headers: getAuthHeaders() })
        .then(res => {
          const counts = {};
          (res.data.accounts || []).forEach(a => { counts[a.session_id] = a.unread_count; });
          setTgUnreadCounts(counts);
        }).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, [chatMode, getAuthHeaders]);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.type === 'new_message') {
      const msg = lastMessage.message;
      // Update conversation list
      setConversations(prev => {
        const idx = prev.findIndex(c => c.id === msg.conversation_id);
        if (idx >= 0) {
          const updated = [...prev];
          const isCurrentChat = selectedConvo?.id === msg.conversation_id;
          updated[idx] = { ...updated[idx], last_message: msg.text, last_activity: msg.timestamp,
            unread_count: msg.direction === 'in' && !isCurrentChat ? (updated[idx].unread_count || 0) + 1 : updated[idx].unread_count };
          updated.sort((a, b) => { if (a.is_pinned && !b.is_pinned) return -1; if (!a.is_pinned && b.is_pinned) return 1; return new Date(b.last_activity) - new Date(a.last_activity); });
          return updated;
        }
        return prev;
      });

      // Show notification for incoming messages not in current view
      if (msg.direction === 'in' && selectedConvo?.id !== msg.conversation_id) {
        // In-app toast notification
        toast('Yeni mesaj geldi', { description: msg.sender?.display_name || 'Bilinmeyen' });
        // Browser notification
        if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
          try { new Notification('Yeni mesaj geldi', { body: '2in1Tech', icon: '/favicon.ico', tag: 'new-msg' }); } catch {}
        }
      }
    }
    if (lastMessage.type === 'notification' && lastMessage.text) {
      // Notification event from WebSocket
      if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
        try { new Notification(lastMessage.text, { body: '2in1Tech', icon: '/favicon.ico', tag: 'notif' }); } catch {}
      }
    }
    if (lastMessage.type === 'force_logout') { toast.error('Hesabınız yönetici tarafından kilitlendi'); localStorage.clear(); navigate('/'); }
  }, [lastMessage, selectedConvo, navigate]);

  const handleConvoAction = async (action, convoId) => {
    try {
      await axios.post(`${API}/conversations/${convoId}/${action}`, {}, { headers: getAuthHeaders() });
      fetchConversations();
      const msgs = { pin: 'Sabitlendi', unpin: 'Sabitleme kaldırıldı', favorite: 'Favorilere eklendi', unfavorite: 'Favorilerden çıkarıldı', 'mark-unread': 'Okunmamış olarak işaretlendi' };
      toast.success(msgs[action] || 'Tamam');
    } catch { toast.error('İşlem başarısız'); }
  };

  const handleSelectConvo = (convo) => {
    setSelectedConvo(convo);
    setShowChat(true);
    // Immediately reset unread in UI
    setConversations(prev => prev.map(c => c.id === convo.id ? { ...c, unread_count: 0, manual_unread: false } : c));
    // Also tell backend to mark as read
    axios.post(`${API}/conversations/${convo.id}/mark-read`, {}, { headers: getAuthHeaders() }).catch(() => {});
  };
  const handleBackToList = () => { setShowChat(false); setSelectedConvo(null); fetchConversations(); };
  const handleLogout = async () => { await logout(); navigate('/'); };
  const handleAppUnlock = () => { setAppLocked(false); sessionStorage.setItem('app_unlocked_at', Date.now().toString()); };

  const filteredConvos = conversations.filter(c => c.title?.toLowerCase().includes(searchQuery.toLowerCase()) || c.last_message?.toLowerCase().includes(searchQuery.toLowerCase()));
  const pinnedConvos = filteredConvos.filter(c => c.is_pinned);
  const favoriteConvos = filteredConvos.filter(c => c.is_favorite && !c.is_pinned);
  const regularConvos = filteredConvos.filter(c => !c.is_pinned && !c.is_favorite);

  const formatTime = (ts) => { if (!ts) return ''; const d = new Date(ts); const now = new Date(); const diff = now - d;
    if (diff < 60000) return 'şimdi'; if (diff < 3600000) return `${Math.floor(diff / 60000)}dk`; if (diff < 86400000) return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }); return d.toLocaleDateString('tr-TR', { month: 'short', day: 'numeric' }); };
  const getInitials = (name) => { if (!name) return '?'; return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2); };
  const getAvatarColor = (name) => { const colors = ['bg-blue-600','bg-emerald-600','bg-amber-600','bg-rose-600','bg-violet-600','bg-cyan-600','bg-orange-600','bg-teal-600']; let hash = 0; for (let i = 0; i < (name||'').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash); return colors[Math.abs(hash) % colors.length]; };

  if (appLocked) return <AppLockScreen onUnlock={handleAppUnlock} />;

  const renderConvoItem = (convo) => (
    <div key={convo.id} className={`flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors cursor-pointer ${selectedConvo?.id === convo.id ? 'bg-secondary/70' : ''}`}>
      <div className="flex-1 flex items-center gap-3 min-w-0" onClick={() => handleSelectConvo(convo)} data-testid="chat-list-item">
        <Avatar className="w-11 h-11 flex-shrink-0"><AvatarFallback className={`${getAvatarColor(convo.title)} text-white text-sm font-medium`}>{getInitials(convo.title)}</AvatarFallback></Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="font-medium text-sm truncate flex items-center gap-1">
              {convo.is_pinned && <Pin className="w-3 h-3 text-primary flex-shrink-0" />}
              {convo.is_favorite && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 flex-shrink-0" />}
              {convo.title}
            </span>
            <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">{formatTime(convo.last_activity)}</span>
          </div>
          <div className="flex items-center justify-between mt-0.5">
            <p className="text-sm text-muted-foreground truncate">
              {convo.draft ? <span className="text-destructive">Taslak: {convo.draft}</span> : (convo.last_message || 'Mesaj yok')}
            </p>
            {((convo.unread_count || 0) > 0 || convo.manual_unread) && (
              <Badge className="ml-2 h-5 min-w-5 flex items-center justify-center text-xs px-1.5 bg-primary text-primary-foreground rounded-full flex-shrink-0">{convo.unread_count || '!'}</Badge>
            )}
          </div>
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg flex-shrink-0"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-xl">
          <DropdownMenuItem onClick={() => handleConvoAction(convo.is_pinned ? 'unpin' : 'pin', convo.id)}><Pin className="w-3 h-3 mr-2" /> {convo.is_pinned ? 'Sabitlemeyi Kaldır' : 'Başa Sabitle'}</DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleConvoAction(convo.is_favorite ? 'unfavorite' : 'favorite', convo.id)}><Star className="w-3 h-3 mr-2" /> {convo.is_favorite ? 'Favorilerden Çıkar' : 'Favorilere Ekle'}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => handleConvoAction('mark-unread', convo.id)}><FileText className="w-3 h-3 mr-2" /> Okunmamış Olarak İşaretle</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const renderSection = (title, convos) => {
    if (convos.length === 0) return null;
    return (<div key={title}><div className="px-4 py-1.5 bg-secondary/30"><span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{title}</span></div>{convos.map(renderConvoItem)}</div>);
  };

  return (
    <div className="h-dvh bg-background flex flex-col overflow-hidden">
      {/* Top header bar */}
      <div className="flex-shrink-0 bg-card/95 backdrop-blur border-b border-border/70 px-3 sm:px-4 py-2 sm:py-3 z-20">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
            <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
            <h1 className="text-base sm:text-lg font-semibold font-display truncate">Mesajlar</h1>
            {connectionState === 'connected' ? <Wifi className="w-3 h-3 text-green-500 flex-shrink-0" title="Bağlı" /> :
             connectionState === 'reconnecting' ? <WifiOff className="w-3 h-3 text-yellow-500 animate-pulse flex-shrink-0" title="Yeniden Bağlanıyor" /> :
             connectionState === 'connecting' ? <Wifi className="w-3 h-3 text-yellow-500 animate-pulse flex-shrink-0" title="Bağlanıyor" /> :
             <WifiOff className="w-3 h-3 text-muted-foreground flex-shrink-0" title="Bağlantı Kesildi" />}
            {/* Account Selector - compact on mobile */}
            {chatMode === 'user' && tgAccounts.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-xl h-6 sm:h-7 text-[10px] sm:text-[11px] gap-1 max-w-[120px] sm:max-w-[180px]">
                    <Phone className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{selectedTgAccount === 'all' ? 'Tümü' : (() => { const a = tgAccounts.find(x => x.id === selectedTgAccount); return a ? `@${a.tg_username || a.tg_first_name || '?'}` : 'Seç'; })()}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="rounded-xl w-56">
                  <DropdownMenuItem onClick={() => setSelectedTgAccount('all')} className={selectedTgAccount === 'all' ? 'bg-secondary/70' : ''}>
                    <Users className="w-3 h-3 mr-2" /> Tüm Hesaplar
                    {Object.values(tgUnreadCounts).reduce((s, c) => s + c, 0) > 0 && (
                      <Badge className="ml-auto text-[9px] h-4 min-w-4 px-1 bg-destructive text-white rounded-full">
                        {Object.values(tgUnreadCounts).reduce((s, c) => s + c, 0)}
                      </Badge>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {tgAccounts.map(acc => (
                    <DropdownMenuItem key={acc.id} onClick={() => setSelectedTgAccount(acc.id)}
                      className={selectedTgAccount === acc.id ? 'bg-secondary/70' : ''}>
                      <Phone className="w-3 h-3 mr-2 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{acc.tg_first_name || ''} {acc.tg_last_name || ''}</p>
                        <p className="text-[10px] text-muted-foreground">{acc.tg_username ? `@${acc.tg_username}` : `+${acc.tg_phone || acc.phone || '?'}`}</p>
                      </div>
                      {(tgUnreadCounts[acc.id] || 0) > 0 && (
                        <Badge className="ml-1 text-[9px] h-4 min-w-4 px-1 bg-destructive text-white rounded-full">
                          {tgUnreadCounts[acc.id]}
                        </Badge>
                      )}
                      {selectedTgAccount === acc.id && <Badge className="ml-1 text-[9px] h-4 bg-primary text-primary-foreground">Aktif</Badge>}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <PanicButton inHeader={true} />
            {chatMode === 'bot' && (
              <Button variant="ghost" size="icon" className="rounded-xl h-8 w-8 sm:h-9 sm:w-9" onClick={() => setShowSearch(true)} data-testid="search-messages-button"><Search className="w-4 h-4 sm:w-5 sm:h-5" /></Button>
            )}
            <NotificationBell wsMessage={lastMessage} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="rounded-xl h-8 w-8 sm:h-9 sm:w-9"><User className="w-4 h-4 sm:w-5 sm:h-5" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl">
                <DropdownMenuItem disabled className="text-xs text-muted-foreground">{user?.email}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowSettings(true)}><Settings className="w-4 h-4 mr-2" /> Ayarlar</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/app/devices')}><Shield className="w-4 h-4 mr-2" /> Cihazlarım</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/app/telegram-link')}><MessageCircle className="w-4 h-4 mr-2" /> Telegram Bağlantısı (Bot)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/app/telegram-user-login')}><Phone className="w-4 h-4 mr-2" /> Telegram Hesabı</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/app/quick-send')}><Zap className="w-4 h-4 mr-2" /> Numaramı Paylaş</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive"><LogOut className="w-4 h-4 mr-2" /> Çıkış Yap</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {/* Mode Toggle — compact on mobile */}
        <div className="flex rounded-xl bg-secondary/50 p-0.5">
          <button onClick={() => setChatMode('accounts')}
            className={`flex-1 text-[11px] sm:text-xs py-1 sm:py-1.5 rounded-lg font-medium transition-colors relative ${chatMode === 'accounts' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            Hesaplar
          </button>
          <button onClick={() => setChatMode('user')}
            className={`flex-1 text-[11px] sm:text-xs py-1 sm:py-1.5 rounded-lg font-medium transition-colors relative ${chatMode === 'user' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="telegram-user-mode-tab">
            Telegram
            {Object.values(tgUnreadCounts).reduce((s, c) => s + c, 0) > 0 && (
              <span className="absolute -top-1 -right-1 h-4 min-w-4 flex items-center justify-center text-[9px] px-1 bg-destructive text-white rounded-full">
                {Object.values(tgUnreadCounts).reduce((s, c) => s + c, 0)}
              </span>
            )}
          </button>
          <button onClick={() => setChatMode('whatsapp')}
            className={`flex-1 text-[11px] sm:text-xs py-1 sm:py-1.5 rounded-lg font-medium transition-colors ${chatMode === 'whatsapp' ? 'bg-green-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}>
            WhatsApp
          </button>
        </div>
      </div>

      {/* ===== HESAPLAR (ACCOUNTS) MODE ===== */}
      {chatMode === 'accounts' && (
        <div className="flex-1 overflow-y-auto" style={{ minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
          <div className="max-w-lg mx-auto p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">Telegram Hesapları</h2>
              <Badge variant="outline" className="text-[10px]">{tgAccounts.length} hesap</Badge>
            </div>

            {tgAccounts.length === 0 ? (
              <div className="bg-card rounded-xl border border-border/70 p-6 text-center space-y-3">
                <Phone className="w-10 h-10 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">Henüz bağlı Telegram hesabı yok</p>
                <Button className="rounded-xl" onClick={() => navigate('/app/telegram-user-login')}>
                  <Phone className="w-4 h-4 mr-2" /> Telegram Hesabı Bağla
                </Button>
              </div>
            ) : (
              <>
                {tgAccounts.map(acc => (
                  <div key={acc.id} className="bg-card rounded-xl border border-border/70 p-4 flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${acc.is_active_account ? 'bg-green-500/20' : 'bg-secondary'}`}>
                      <Phone className={`w-5 h-5 ${acc.is_active_account ? 'text-green-500' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {acc.tg_first_name || ''} {acc.tg_last_name || ''} {acc.tg_username ? `(@${acc.tg_username})` : ''}
                      </p>
                      <p className="text-[10px] text-muted-foreground">+{acc.tg_phone || acc.phone || '?'}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {acc.is_active_account && <Badge className="text-[9px] h-4 bg-green-500 text-white">Aktif</Badge>}
                        <Badge variant="secondary" className="text-[9px] h-4">Bağlı</Badge>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {(tgUnreadCounts[acc.id] || 0) > 0 && (
                        <Badge className="h-5 min-w-5 flex items-center justify-center text-[10px] px-1.5 bg-destructive text-white rounded-full">
                          {tgUnreadCounts[acc.id]}
                        </Badge>
                      )}
                      {!acc.is_active_account && (
                        <Button variant="outline" size="sm" className="h-6 text-[10px] rounded-lg px-2"
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/telegram-user/set-active/${acc.id}`, {}, { headers: getAuthHeaders() });
                              // Refresh accounts
                              const res = await axios.get(`${API}/telegram-user/accounts`, { headers: getAuthHeaders() });
                              setTgAccounts((res.data.accounts || []).filter(a => a.status === 'active'));
                              toast.success('Aktif hesap değiştirildi');
                            } catch { toast.error('Değiştirilemedi'); }
                          }}>
                          Geçiş
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <Button variant="outline" className="w-full rounded-xl" onClick={() => navigate('/app/telegram-user-login')}>
                  <Phone className="w-4 h-4 mr-2" /> Yeni Hesap Ekle
                </Button>
              </>
            )}

            {/* Quick actions */}
            <div className="space-y-2 pt-2">
              <Button variant="outline" className="w-full rounded-xl text-xs justify-start" onClick={() => navigate('/app/telegram-link')}>
                <MessageCircle className="w-4 h-4 mr-2" /> Telegram Bot Bağlantısı
              </Button>
              <Button variant="outline" className="w-full rounded-xl text-xs justify-start" onClick={() => navigate('/app/quick-send')}>
                <Zap className="w-4 h-4 mr-2" /> Numaramı Paylaş
              </Button>
              <Button variant="outline" className="w-full rounded-xl text-xs justify-start" onClick={() => navigate('/app/devices')}>
                <Shield className="w-4 h-4 mr-2" /> Cihazlarım
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== TELEGRAM USER MODE ===== */}
      {chatMode === 'user' && (
        <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
          <TelegramUserChats selectedAccount={selectedTgAccount} accounts={tgAccounts} />
        </div>
      )}

      {/* ===== WHATSAPP MODE ===== */}
      {chatMode === 'whatsapp' && (
        <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
          <WhatsAppWebView />
        </div>
      )}

      {showSearch && <SearchOverlay onClose={() => setShowSearch(false)} />}
      {showSettings && <SettingsDialog open={showSettings} onClose={() => setShowSettings(false)} />}
    </div>
  );
}
